// Common

import { flatten } from "lodash-es";
import { type Ref, type RefObject, useEffect, useImperativeHandle, useMemo, useState } from "react";

import { Problem } from "@mpa/toolbox/error/Problem";

// Field Id

export const useFieldId = (props: AnyFieldProps<any>) => {
    const id = useMemo(() => `field-${props.id ? props.id : crypto.randomUUID()}`, [props.id]);
    return id;
};

// Props

export type AnyFieldProps<V> = {
    id?: string;
    label?: string;
    value: V | null | undefined;
    onChange: (value: V | null) => unknown;
    validator?: ValidatorFn<V> | Array<ValidatorFn<V>>;
    ref?: Ref<AnyFieldRef<V>>;
    required?: boolean;
    onValidityChange?: (validity: Validity) => unknown;
};

// Validity

export type ValidatorFn<V> = { name: string; fn: (value: V) => void };
export function validationFn<V>(name: string, fn: (value: V) => void): ValidatorFn<V> {
    return { name, fn };
}

export type Validity = { valid: false; error: unknown } | { valid: true };

const EMPTY_VALIDATORS: Array<ValidatorFn<unknown>> = [];
export const VALIDITY_OK: Validity = { valid: true };

function mergeValidators<T>(
    ...args: Array<ValidatorFn<T> | Array<ValidatorFn<T>> | undefined>
): ValidatorFn<T> {
    const validators = flatten(args).filter((v) => v !== undefined);
    return validationFn(validators.map((v) => v.name).join(","), (value) => {
        for (const validator of validators) validator.fn(value);
    });
}

function computeValidity<T>(
    value: T | null | undefined,
    required: boolean | undefined,
    validator: ValidatorFn<T>,
): Validity {
    if (value === null || value === undefined) {
        return required ? { valid: false, error: new Error("Champ requis") } : VALIDITY_OK;
    }
    try {
        validator.fn(value);
        return VALIDITY_OK;
    } catch (error) {
        return { valid: false, error };
    }
}

export function useValidity<T>(
    props: AnyFieldProps<T>,
    otherValidators: Array<ValidatorFn<T>> = EMPTY_VALIDATORS,
): Validity {
    const { value, validator, required, onValidityChange } = props;
    const fullValidator = mergeValidators(otherValidators, validator);

    const validity = useMemo(
        () => computeValidity(value, required, fullValidator),
        [value, required, fullValidator.name],
    );

    useEffect(() => {
        if (onValidityChange) onValidityChange(validity);
    }, [validity, onValidityChange]);

    return validity;
}

// Error

export function getErrorMessage(error: unknown) {
    if (error instanceof Problem) {
        return error.params.explanation;
    }
    if (error instanceof Error) {
        return error.message;
    }
    return "Une erreur est survenue";
}

export type RChangeEvent<V> = {
    name: string;
    value: V;
    event?: React.ChangeEvent<HTMLInputElement>;
};

// Dirtyness

export function useFieldDirtyness<T>(props: AnyFieldProps<T>) {
    const { value } = props;
    const wasInitialValueUndefined = useMemo(() => value === undefined, []);
    const [hasBeenBlured, setHasBeenBlured] = useState(false);
    const isDirty = !wasInitialValueUndefined || (wasInitialValueUndefined && hasBeenBlured);
    return { isDirty, setDirty: () => setHasBeenBlured(true) };
}

// Handle

export type AnyFieldRef<V> = {
    validate: () => V | null;
    focus: () => void;
};

type FieldImperativeHandleInput = {
    validity: Validity;
    setDirty: () => void;
    inputRef: RefObject<HTMLInputElement | null>;
};

export function useFieldImperativeHandle<V>(
    props: AnyFieldProps<V>,
    config: FieldImperativeHandleInput,
) {
    const { value } = props;
    const { validity, setDirty, inputRef } = config;
    return useImperativeHandle(
        props.ref,
        () => {
            return {
                validate: () => {
                    setDirty();
                    if (!validity.valid) throw validity.error;
                    return value as V | null;
                },
                focus: () => {
                    if (!inputRef.current) return;
                    inputRef.current.focus();
                },
            };
        },
        [value, validity, setDirty, inputRef],
    );
}
